import { motion } from "framer-motion";
import { useEffect, useState } from "react";

const GateArt = () => (
  <svg viewBox="0 0 400 480" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" preserveAspectRatio="none" aria-hidden="true">
    {/* crenellations */}
    {[70, 130, 240, 300].map((x) => (
      <rect key={x} x={x} y={18} width={30} height={26} stroke="currentColor" strokeWidth="1" />
    ))}
    {/* arch */}
    <path d="M30 470 V200 Q30 60 200 60 Q370 60 370 200 V470" stroke="currentColor" strokeWidth="1.2" />
    {/* portcullis verticals */}
    {[66, 100, 134, 168, 202, 236, 270, 304, 338].map((x) => (
      <line key={x} x1={x} y1={110} x2={x} y2={470} stroke="currentColor" strokeWidth="0.8" />
    ))}
    {/* horizontals */}
    {[150, 210, 270, 330, 390, 440].map((y) => (
      <line key={y} x1={30} y1={y} x2={370} y2={y} stroke="currentColor" strokeWidth="0.8" />
    ))}
    {/* center seam */}
    <line x1={200} y1={62} x2={200} y2={470} stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

export default function CastleGate({ seat, inviteLink, alreadyRegistered, emailSent }) {
  const [stage, setStage] = useState("closed");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStage("open"), 1300);
    return () => clearTimeout(t);
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy your invite link:", inviteLink);
    }
  };

  const open = stage === "open";

  return (
    <div data-testid="gate-success" className="relative py-6">
      <div className="relative mx-auto max-w-lg min-h-[480px] flex items-center justify-center overflow-hidden">
        {/* Revealed content behind the gate */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={open ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.9, ease: [0.33, 1, 0.68, 1] }}
          className="relative z-0 text-center px-6 py-14 w-full"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/10 to-transparent blur-2xl pointer-events-none" aria-hidden="true" />
          <p className="font-mono text-xs tracking-[0.35em] text-gold mb-6" data-testid="gate-youre-in">
            {alreadyRegistered ? "WELCOME BACK" : "YOU'RE IN"}
          </p>
          <p className="font-serif text-5xl sm:text-6xl font-light mb-2" data-testid="seat-number">
            Seat <span className="italic text-gold">#{seat}</span>
          </p>
          <p className="text-muted-foreground text-sm mb-10">
            of 100 founding seats — free forever{emailSent ? ". Your Load Profile is on its way to your inbox" : ""}
          </p>

          <div className="border border-border bg-secondary/30 p-4 flex items-center gap-3 mb-4">
            <span data-testid="invite-link" className="font-mono text-xs text-bone/85 truncate flex-1 text-left">
              {inviteLink}
            </span>
            <button
              data-testid="copy-invite-button"
              onClick={copyLink}
              className="shrink-0 font-mono text-[11px] tracking-[0.15em] border border-gold/60 text-gold px-3 py-2 hover:bg-gold hover:text-background transition-colors duration-300"
            >
              {copied ? "COPIED" : "COPY"}
            </button>
          </div>
          <p className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground" data-testid="invites-remaining">
            YOU HAVE 3 INVITES. SPEND THEM ON PEOPLE WHO CARRY THE SAME WEIGHT.
          </p>
        </motion.div>

        {/* The gate: two halves sliding apart */}
        <div className="absolute inset-0 z-10 pointer-events-none" aria-hidden={open}>
          <motion.div
            initial={{ x: 0 }}
            animate={open ? { x: "-104%" } : {}}
            transition={{ duration: 1.7, ease: [0.6, 0, 0.2, 1] }}
            className="absolute left-0 top-0 w-1/2 h-full overflow-hidden bg-card border border-gold/30 border-r-0"
          >
            <div className="absolute left-0 top-0 w-[200%] h-full text-gold/80 p-6">
              <GateArt />
            </div>
          </motion.div>
          <motion.div
            initial={{ x: 0 }}
            animate={open ? { x: "104%" } : {}}
            transition={{ duration: 1.7, ease: [0.6, 0, 0.2, 1] }}
            className="absolute right-0 top-0 w-1/2 h-full overflow-hidden bg-card border border-gold/30 border-l-0"
          >
            <div className="absolute right-0 top-0 w-[200%] h-full text-gold/80 p-6">
              <GateArt />
            </div>
          </motion.div>
          {/* Shield mark over the seam */}
          <motion.img
            src="/keep-mark.png"
            alt=""
            initial={{ opacity: 1, scale: 1 }}
            animate={open ? { opacity: 0, scale: 0.7 } : {}}
            transition={{ duration: 0.6 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-auto z-20"
          />
          <motion.p
            animate={open ? { opacity: 0 } : { opacity: [0.4, 1, 0.4] }}
            transition={open ? { duration: 0.4 } : { duration: 1.6, repeat: Infinity }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] text-gold/70 whitespace-nowrap"
          >
            THE GATE OPENS…
          </motion.p>
        </div>
      </div>
    </div>
  );
}
